import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  Autocomplete,
} from "@mui/material";
import { LedgerFiltersProps } from "../types";
import { BranchSelector } from "../../../components/BranchSelector";
import { useBranch } from "../../../components/BranchContext";

export const LedgerFilters: React.FC<LedgerFiltersProps> = ({
  selectedAccountId,
  onAccountChange,
  filters,
  onFiltersChange,
  onResetFilters,
  postingAccounts,
}) => {
  const { selectedBranch } = useBranch();

  const sourceModules = [
    { value: "", label: "All Sources" },
    { value: "POS", label: "POS" },
    { value: "Payroll", label: "Payroll" },
    { value: "Procurement", label: "Procurement" },
    { value: "Manual", label: "Manual" },
    { value: "Adjustment", label: "Adjustment" },
  ];

  const handleFilterChange = (field: string, value: string) => {
    onFiltersChange({ [field]: value });
  };

  return (
    <Stack spacing={3}>
      {/* Branch Override */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Branch Override
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", mb: 2, display: "block" }}
        >
          Override global branch selection for this ledger view
        </Typography>
        <BranchSelector
          selectedBranch={
            filters.selectedBranch || selectedBranch || "organization"
          }
          onBranchChange={(branchId) =>
            handleFilterChange(
              "selectedBranch",
              branchId === "organization" ? "" : branchId
            )
          }
          size="small"
          showLabel={false}
        />
      </Box>

      {/* Account Selection */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Account
        </Typography>
        <Autocomplete
          fullWidth
          size="small"
          options={postingAccounts}
          getOptionLabel={(option) =>
            `${option.accountCode} - ${option.accountName}`
          }
          value={
            postingAccounts.find((acc) => acc.id === selectedAccountId) || null
          }
          onChange={(_, value) => onAccountChange(value?.id || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Account"
              placeholder="Choose an account..."
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {option.accountCode} - {option.accountName}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {option.accountType} • Balance:{" "}
                  {option.currentBalance?.toLocaleString() || "0"}
                </Typography>
              </Box>
            </Box>
          )}
        />
      </Box>

      {/* Date Range */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Date Range
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="From"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="To"
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Source & Search */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Source & Search
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Source Module</InputLabel>
            <Select
              value={filters.sourceModule}
              label="Source Module"
              onChange={(e) =>
                handleFilterChange("sourceModule", e.target.value)
              }
            >
              {sourceModules.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Journal ID"
            value={filters.journalEntryId}
            onChange={(e) =>
              handleFilterChange("journalEntryId", e.target.value)
            }
            placeholder="JE-2024-0001"
            size="small"
          />

          <TextField
            fullWidth
            label="Search Keywords"
            value={filters.referenceSearch}
            onChange={(e) =>
              handleFilterChange("referenceSearch", e.target.value)
            }
            placeholder="Reference, narration, etc."
            size="small"
          />
        </Stack>
      </Box>
    </Stack>
  );
};
